import styled from 'styled-components';
import Text from 'src/design/styleguide/text/Text';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { breakpointSmall } from 'src/design/styleguide/common/breakpoints';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import {
  greenDark,
  greenLight,
  pink50,
  pink700,
  purple50,
  purple700,
  yellow100,
} from 'src/themes/brickx/colors';

export const StatusPill = styled.span`
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  padding: 2px ${gridUnit * 2}px;
  border-radius: 3px;
  background: var(--bx-color---grey-100);
  color: var(--bx-color---grey-700);
  font-size: 1.2rem;
  font-weight: 600;
  line-height: 1;
  margin: 0 0 0 ${gridUnit * 2}px;

  &.positive {
    background: ${greenLight};
    color: ${greenDark};
  }

  &.negative {
    background: ${pink50};
    color: ${pink700};
  }

  &.info {
    background: var(--bx-color---blue-50);
    color: var(--bx-color---blue-700);
  }
`;

export const AccountTypeBadge = styled(StatusPill)`
  margin-left: 0;
  font-weight: 500;

  &.individual {
    background: var(--bx-color---purple-50);
    color: var(--bx-color---purple-600);
  }

  &.business {
    background: var(--bx-color---blue-50);
    color: var(--bx-color---blue-700);
  }

  &.minor {
    background: var(--bx-color---yellow-50);
    color: var(--bx-color---yellow-800);
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--bx-color---grey-300);
  border-radius: var(--bx-radius);
  background: var(--bx-color---surface);
  color: var(--bx-color---grey-800);
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    border-color: var(--bx-color---purple-400);
    color: ${themeForAdvisorAccount.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid var(--bx-color---purple-300);
    outline-offset: 2px;
  }

  &:disabled {
    border-color: var(--bx-color---grey-300);
    color: var(--bx-color---grey-500);
    cursor: default;
  }
`;

export const Card = styled.section`
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--bx-color---grey-200);
  border-radius: var(--bx-radius);
  background: var(--bx-color---surface);
  box-shadow: var(--bx-shadow);
  --advisor-card-gutter: ${gridUnit * 4}px;

  ${breakpointSmall(`
    --advisor-card-gutter: ${gridUnit * 6}px;
  `)}
`;

export const CardHeader = styled.header`
  display: flex;
  min-height: 64px;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: ${gridUnit * 4}px;
  padding: ${gridUnit * 6}px var(--advisor-card-gutter);
`;

export const CardTitle = styled(Text).attrs({
  as: 'h2',
  fontSize: '2x-small',
  fontWeight: 'medium',
})`
  margin: 0;
  color: var(--bx-color---grey-800);
  font-size: 1.7rem;
`;

export const DetailLabel = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
})`
  margin: 0;
  color: var(--bx-color---grey-700);
`;

export const DetailValue = styled(Text).attrs({
  as: 'p',
  fontSize: '2x-small',
  fontWeight: 'bold',
})`
  margin: ${gridUnit * 2}px 0 0;
  color: var(--bx-color---grey-800);
  font-size: 1.8rem;

  &.positive {
    color: ${greenDark};
  }

  &.negative {
    color: ${pink700};
  }
`;

export const EmptyCopy = styled(Text).attrs({ as: 'p', fontSize: '4x-small' })`
  margin: 0;
  padding: ${gridUnit * 6}px var(--advisor-card-gutter);
  color: var(--bx-color---grey-700);
`;

export const TransactionAmount = styled.span`
  white-space: nowrap;

  &.positive {
    color: ${greenDark};
  }

  &.negative {
    color: ${pink700};
  }
`;

export const DetailList = styled.dl`
  display: grid;
  gap: ${gridUnit * 4}px;
  margin: 0;
  padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;
`;

export const DetailRow = styled.div`
  display: grid;
  grid-template-columns: minmax(96px, 2fr) minmax(0, 3fr);
  gap: ${gridUnit * 4}px;
  align-items: start;
`;

export const DetailTerm = styled(Text).attrs({
  as: 'dt',
  fontSize: '4x-small',
})`
  margin: 0;
  color: var(--bx-color---grey-700);
`;

export const DetailDescription = styled(Text).attrs({
  as: 'dd',
  fontSize: '4x-small',
  fontWeight: 'medium',
})`
  min-width: 0;
  margin: 0;
  color: var(--bx-color---grey-800);
  overflow-wrap: anywhere;
`;

export const DocumentRows = styled.div`
  padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;
`;

export const DocumentIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${gridUnit * 3}px;
`;

export const DocumentGlyph = styled.span`
  display: inline-flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: var(--bx-color---purple-50);
  color: ${themeForAdvisorAccount.colors.primary};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const DocumentRow = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: ${gridUnit * 3}px;
  padding: ${gridUnit * 3}px 0;
  border-bottom: 1px solid var(--bx-color---grey-200);

  &:last-child {
    border-bottom: 0;
  }
`;

export const DocumentName = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
  fontWeight: 'medium',
})`
  min-width: 0;
  margin: 0;
  color: var(--bx-color---grey-800);
`;

export const DocumentMeta = styled(Text).attrs({
  as: 'p',
  fontSize: '5x-small',
})`
  margin: ${gridUnit}px 0 0;
  color: var(--bx-color---grey-500);
`;

export const DownloadButton = styled(IconButton)`
  width: 36px;
  height: 36px;
  flex-basis: 36px;

  &:disabled {
    cursor: pointer;
    opacity: 0.5;
  }

  &[aria-busy='true'] svg {
    animation: advisor-document-spin 1s linear infinite;
  }

  @keyframes advisor-document-spin {
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SearchField = styled.label`
  position: relative;
  display: flex;
  width: 100%;
  max-width: 360px;
  align-items: center;

  svg {
    position: absolute;
    left: ${gridUnit * 3}px;
    color: var(--bx-color---grey-500);
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 42px;
    box-sizing: border-box;
    padding: 0 ${gridUnit * 3}px 0 ${gridUnit * 9}px;
    border: 1px solid var(--bx-color---grey-300);
    border-radius: var(--bx-radius);
    background: var(--bx-color---surface);
    color: var(--bx-color---grey-800);
    font-family: inherit;
    font-size: 1.3rem;
  }

  input:focus {
    border-color: var(--bx-color---purple-400);
    outline: 2px solid var(--bx-color---purple-100);
  }
`;

const HoldingsTableScroll = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
`;

const HoldingsTable = styled.table`
  width: 100%;
  min-width: 1040px;
  border-collapse: collapse;
  color: var(--bx-color---grey-700);
  font-size: 1.2rem;

  th,
  td {
    padding: ${gridUnit * 4}px ${gridUnit * 3}px;
    border-bottom: 1px solid var(--bx-color---grey-200);
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
  }

  th {
    color: var(--bx-color---grey-500);
    font-size: 1.1rem;
    font-weight: 500;
  }

  th:first-child,
  td:first-child {
    padding-left: ${gridUnit * 6}px;
  }

  th:last-child,
  td:last-child {
    padding-right: ${gridUnit * 6}px;
  }

  .numeric {
    text-align: right;
  }
`;

const PaginationFooter = styled.footer`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${gridUnit * 3}px;
  align-items: center;
  padding: ${gridUnit * 6}px ${gridUnit * 4}px;

  &::before {
    position: absolute;
    top: 0;
    right: ${gridUnit * 4}px;
    left: ${gridUnit * 4}px;
    border-top: 1px solid var(--bx-color---grey-100);
    content: '';
  }

  ${breakpointSmall(`
    flex-direction: row;
    padding-right: ${gridUnit * 6}px;
    padding-left: ${gridUnit * 6}px;

    &::before {
      right: ${gridUnit * 6}px;
      left: ${gridUnit * 6}px;
    }
  `)}
`;

export const PageInfo = styled(Text).attrs({ as: 'p', fontSize: '4x-small' })`
  margin: 0;
  color: var(--bx-color---grey-800);
`;

export const Pagination = styled.nav`
  display: flex;
  align-items: center;
  gap: ${gridUnit * 3}px;
`;

export const PaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 ${gridUnit}px;
  border: 0;
  border-radius: var(--bx-radius);
  background: transparent;
  color: var(--bx-color---grey-700);
  font-family: inherit;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;

  &:hover:not(:disabled):not([aria-current='page']) {
    background: var(--bx-color---purple-50);
    color: ${themeForAdvisorAccount.colors.primary};
  }

  &[aria-current='page'] {
    background: ${themeForAdvisorAccount.colors.primary};
    color: var(--bx-color---surface);
  }
`;

export const PaginationEllipsis = styled.span`
  min-width: 24px;
  color: var(--bx-color---grey-700);
  font-weight: bold;
  text-align: center;
`;

export const DataTabLayout = styled.div`
  display: grid;
  min-width: 0;
  gap: ${gridUnit * 4}px;
`;

export const DataSummaryGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 4}px;
  padding: 0 ${gridUnit * 6}px ${gridUnit * 6}px;

  @media (min-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1000px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    &.four {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`;

export const DataSummaryCard = styled.article`
  display: grid;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${gridUnit * 4}px;
  align-items: center;
  padding: ${gridUnit * 5}px;
  border: 1px solid var(--bx-color---grey-200);
  border-radius: var(--bx-radius);
  background: var(--bx-color---surface);
`;

export const DataSummaryIcon = styled.span`
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--bx-color---purple-50);
  color: ${themeForAdvisorAccount.colors.primary};

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const DataSummaryValue = styled(DetailValue)`
  margin-top: ${gridUnit}px;
  font-size: 2rem;
`;

export const DataToolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 3}px;
  padding: 0 ${gridUnit * 6}px ${gridUnit * 6}px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1100px) {
    grid-template-columns: minmax(220px, 1fr) repeat(3, minmax(140px, auto));
  }
`;

export const FilterSelect = styled.select`
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  padding: 0 ${gridUnit * 8}px 0 ${gridUnit * 3}px;
  border: 1px solid var(--bx-color---grey-300);
  border-radius: var(--bx-radius);
  background: var(--bx-color---surface);
  color: var(--bx-color---grey-800);
  font-family: inherit;
  font-size: 1.3rem;

  &:focus {
    border-color: var(--bx-color---purple-400);
    outline: 2px solid var(--bx-color---purple-100);
  }
`;

export const DataTableScroll = styled(HoldingsTableScroll)`
  border-top: 1px solid var(--bx-color---grey-200);
`;

export const DataTable = styled(HoldingsTable)`
  min-width: 960px;
`;

export const DataFooter = styled(PaginationFooter)``;

export const OrdersLayout = styled(DataTabLayout)`
  ${CardHeader} {
    min-height: 72px;
    padding: ${gridUnit * 6}px var(--advisor-card-gutter);
  }

  ${CardTitle} {
    font-size: 20px;
  }

  ${DataSummaryGrid} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${gridUnit * 4}px;
    padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;
  }

  &[data-summary-layout='inline'] ${DataSummaryGrid} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${DataSummaryCard} {
    gap: ${gridUnit * 3}px;
    min-height: 92px;
    padding: ${gridUnit * 4}px;
    box-sizing: border-box;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

    ${breakpointSmall(`
      gap: ${gridUnit * 4}px;
      padding: ${gridUnit * 5}px;
    `)}
  }

  ${DataSummaryIcon} {
    width: 24px;
    height: 32px;
    border-radius: 0;
    background: transparent;

    svg {
      width: 24px;
      height: 24px;
    }

    ${breakpointSmall(`
      width: 28px;

      svg {
        width: 28px;
        height: 28px;
      }
    `)}
  }

  ${DetailLabel} {
    color: var(--bx-color---grey-800);
    font-size: 1.6rem;
    font-weight: 500;
  }

  ${DataSummaryValue} {
    margin-top: ${gridUnit * 2}px;
    font-size: 2.4rem;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  ${DataToolbar} {
    gap: ${gridUnit * 2}px;
    padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;
    grid-template-columns: minmax(0, 1fr);
  }

  &[data-filter-layout='split'] ${DataToolbar} {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > :last-child {
      grid-column: 2;
      grid-row: 1;
    }
  }

  &[data-filter-layout='wide'] ${DataToolbar} {
    grid-template-columns: minmax(0, 1fr) repeat(3, minmax(0, 176px));
  }

  ${SearchField} {
    min-width: 0;
    max-width: none;

    svg {
      color: var(--bx-color---grey-800);
    }
  }

  ${SearchField} input,
  ${FilterSelect} {
    min-width: 0;
    max-width: 100%;
    height: 40px;
    font-size: 1.4rem;
  }

  ${FilterSelect} {
    appearance: none;
    color: var(--bx-color---grey-700);
  }

  ${SearchField} input:focus,
  ${FilterSelect}:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
    outline: none;
  }

  ${DataTableScroll} {
    width: calc(100% - ${gridUnit * 8}px);
    max-width: calc(100% - ${gridUnit * 8}px);
    margin-right: ${gridUnit * 4}px;
    margin-left: ${gridUnit * 4}px;
    border-top: 0;

    ${breakpointSmall(`
      width: calc(100% - ${gridUnit * 12}px);
      max-width: calc(100% - ${gridUnit * 12}px);
      margin-right: ${gridUnit * 6}px;
      margin-left: ${gridUnit * 6}px;
    `)}

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: var(--bx-color---grey-50);
    }

    &::-webkit-scrollbar-thumb {
      border: 2px solid var(--bx-color---grey-50);
      border-radius: 8px;
      background: var(--bx-color---grey-300);
    }
  }

  ${DataTable} {
    min-width: 0;
    table-layout: auto;
    font-size: 1.6rem;

    th,
    td {
      width: auto;
      padding-right: ${gridUnit * 3}px;
      padding-left: ${gridUnit * 3}px;
    }

    th {
      padding-top: ${gridUnit * 3}px;
      padding-bottom: ${gridUnit * 3}px;
      color: var(--bx-color---grey-800);
      font-size: 1.6rem;
      font-weight: normal;
    }

    td {
      padding-top: ${gridUnit * 2.5}px;
      padding-bottom: ${gridUnit * 2.5}px;
      border-bottom-color: var(--bx-color---grey-100);
      color: var(--bx-color---grey-800);
    }

    tbody tr {
      height: 51px;
      cursor: pointer;
    }

    tbody tr:hover td,
    tbody tr:focus td {
      background: var(--bx-color---grey-50);
    }

    tbody tr:focus-visible {
      outline: 2px solid ${themeForAdvisorAccount.colors.primary};
      outline-offset: -2px;
    }

    th:first-child,
    td:first-child {
      padding-left: 0;
    }

    th:last-child,
    td:last-child {
      width: 1%;
      padding-right: 0;
      text-align: left;
    }

    .numeric {
      text-align: left;
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }
  }

  ${StatusPill} {
    margin: 0;
    padding: ${gridUnit}px ${gridUnit * 2}px;
    border-radius: 4px;
    font-size: 1.4rem;
  }

  ${DataFooter} {
    min-height: 76px;
    box-sizing: border-box;
    padding: ${gridUnit * 6}px var(--advisor-card-gutter);
  }

  ${PageInfo} {
    font-size: 1.6rem;
  }
`;

export const OrdersExport = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${gridUnit * 2}px;
  height: 38px;
  padding: 0 ${gridUnit * 3}px;
  border: 1px solid ${themeForAdvisorAccount.colors.primary};
  border-radius: 4px;
  background: var(--bx-color---surface);
  color: ${themeForAdvisorAccount.colors.primary};
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const OrdersSelectField = styled.label`
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;

  & > svg {
    position: absolute;
    right: ${gridUnit * 3}px;
    color: var(--bx-color---grey-700);
    pointer-events: none;
  }
`;

export const OrdersDateFilter = styled(OrdersSelectField)`
  & > svg:first-child {
    top: 12px;
    left: ${gridUnit * 3}px;
    right: auto;
    color: var(--bx-color---grey-700);
  }

  select {
    padding-left: ${gridUnit * 9}px;
  }
`;

export const OrdersCreated = styled.span`
  font-size: inherit;
  white-space: nowrap;
`;

export const OrdersPropertyIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridUnit * 2}px;
  white-space: nowrap;
`;

export const OrdersPropertyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  color: ${themeForAdvisorAccount.colors.primary};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ReferenceText = styled.span`
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  color: var(--bx-color---grey-800);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TransactionDescription = styled.span`
  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TransactionTypeBadge = styled(StatusPill)`
  &.deposit {
    background: ${yellow100};
    color: #765b00;
  }

  &.withdrawal {
    background: #f3e9df;
    color: #795438;
  }

  &.other {
    background: ${purple50};
    color: ${purple700};
  }
`;
